import { CameraSolidIcon, TrashIcon } from './Icons';

/**
 * #661 (F34) — the dark overlay of a logo with the two round controls: the
 * camera changes the logo and the trash removes it.
 *
 * The caller draws the image (or the empty box) in a parent that has the
 * classes `relative group`, and owns the file input, the confirm and the
 * write. A control is never visible on hover only (#552): on a touch screen
 * the overlay is always on, and a keyboard user reaches the buttons through
 * `group-focus-within`. `lg` is the banner and the modal, `sm` the thumbnail.
 * #663 — on a touch screen (`hover:none`, of any width) the `sm` buttons are
 * 36 px. The caller makes the thumbnail 80 px wide there (36 + gap 8 + 36), so
 * that the two buttons fit.
 *
 * Without `onRemove` the overlay shows the camera only: the caller gives
 * `onRemove` only when a logo exists.
 */
const SIZE = {
  lg: { gap: 'gap-6', button: 'w-12 h-12', icon: 'w-6 h-6' },
  sm: { gap: 'gap-2', button: 'w-7 h-7 [@media(hover:none)]:w-9 [@media(hover:none)]:h-9', icon: 'w-4 h-4' },
} as const;

export default function LogoEditOverlay({ onChange, onRemove, changeDisabled = false, removeDisabled = false, size, changeLabel, removeLabel, className = '' }: {
  onChange: () => void;
  onRemove?: () => void;
  changeDisabled?: boolean;
  removeDisabled?: boolean;
  size: keyof typeof SIZE;
  changeLabel: string;
  removeLabel: string;
  /** For example `rounded-xl`, so that the overlay follows the corners of the image. */
  className?: string;
}) {
  const s = SIZE[size];
  return (
    <div data-testid="logo-edit-overlay"
      className={`absolute inset-0 bg-black/40 opacity-100 [@media(hover:hover)]:opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity flex items-center justify-center ${s.gap} ${className}`.trim()}>
      <button type="button" onClick={onChange} disabled={changeDisabled} title={changeLabel} aria-label={changeLabel}
        data-testid="logo-change"
        className={`${s.button} rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center transition-colors disabled:opacity-50`}>
        <CameraSolidIcon className={`${s.icon} text-white`} />
      </button>
      {onRemove && (
        <button type="button" onClick={onRemove} disabled={removeDisabled} title={removeLabel} aria-label={removeLabel}
          data-testid="logo-remove"
          className={`${s.button} rounded-full bg-white/20 hover:bg-red-500/60 flex items-center justify-center transition-colors disabled:opacity-50`}>
          <TrashIcon className={`${s.icon} text-white`} />
        </button>
      )}
    </div>
  );
}
